import { useState } from 'react';
import {
  CALC_STATUS_LABELS,
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_TYPE_LABELS,
  DOSSIER_SCENARIO_LABELS,
  PDFA_STATUS_LABELS,
  type DocumentDto,
  type DocumentStatus,
  type DocumentType,
  type DossierScenario,
  type DossierStatut,
  type PdfaStatus,
  type PvVariant,
  type PvVariantOption,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { useApi } from '../../lib/ApiProvider';
import { useCalculations } from '../calculations/hooks';
import { PdfaPipelineSection } from './PdfaPipelineSection';
import {
  useDocumentManifest,
  useDocuments,
  useFinalizeDocument,
  useGenerateDocument,
  useGenerateScenario,
  useGenerationRuns,
  usePipelineStatus,
  usePvVariants,
  useTemplates,
} from './hooks';
import { useUpdateDossier } from '../dossiers/hooks';

const DOCUMENT_TYPES: DocumentType[] = [
  'PV_BATI',
  'PV_EXPERTISE_COMPLETE',
  'RAPPORT_EXPERTISE',
  'DOCUMENT_METHODE_SPECIFIQUE',
];

/**
 * Document types the server produces for the scenarios whose set is variant-INDEPENDENT.
 *
 * PV_SEUL is deliberately absent: since LOT 9A its document type depends on the selected PV variant,
 * which only the server knows (`GET /dossiers/:id/documents/pv-variants`). Mirroring that mapping
 * here would re-derive a business rule client-side and drift the moment a variant changes.
 */
const SCENARIO_DOCUMENTS: Record<Exclude<DossierScenario, 'PV_SEUL'>, DocumentType[]> = {
  RAPPORT_SEUL: ['RAPPORT_EXPERTISE'],
  PV_RAPPORT: ['PV_EXPERTISE_COMPLETE', 'RAPPORT_EXPERTISE'],
};

const LOCKED_STATUSES: DossierStatut[] = ['FINALISE', 'ARCHIVE'];

function money(value: string | null | undefined): string {
  return value && value.length > 0 ? `${value} FCFA` : '—';
}

function statusTone(status: DocumentStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'ANNULE':
    case 'PDFA_VALIDATION_FAILED':
      return 'danger';
    case 'PDFA_VALID':
    case 'FINALISE':
      return 'success';
    case 'DOCX_GENERATED':
    case 'PDFA_VALIDATION_PENDING':
      return 'info';
    case 'REMPLACE':
    case 'ARCHIVE':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** PDF/A is always NOT_GENERATED in LOT 7A: the pipeline lands in LOT 7B. */
function pdfaTone(status: PdfaStatus): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'VALID':
      return 'success';
    case 'VALIDATION_FAILED':
      return 'danger';
    case 'NOT_GENERATED':
      return 'neutral';
    default:
      return 'warning';
  }
}

/** Small labelled key/value used across the result card. */
function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd
        className={mono ? 'break-all font-mono text-xs text-slate-800' : 'text-sm text-slate-800'}
      >
        {value && value.length > 0 ? value : '—'}
      </dd>
    </div>
  );
}

/**
 * « Documents » — génération documentaire d'un dossier (LOT 7A, DOCX uniquement). L'expert soumet
 * son INTENTION (type de document + calcul validé) ; le SERVEUR construit le manifeste et calcule le
 * hash officiel. Le Web ne construit JAMAIS le manifeste ni le hash : il n'affiche que les valeurs
 * renvoyées par l'API. Le PDF/A (production + validation) est traité en LOT 7B.
 */
export function DocumentsTab({
  dossierId,
  scenario,
  statut,
}: {
  dossierId: string;
  scenario?: DossierScenario;
  statut?: DossierStatut;
}): JSX.Element {
  const api = useApi();
  const calculations = useCalculations(dossierId);
  const templates = useTemplates();
  const documents = useDocuments(dossierId);
  const runs = useGenerationRuns(dossierId);
  const generate = useGenerateDocument(dossierId);
  const generateScenario = useGenerateScenario(dossierId);
  const finalize = useFinalizeDocument(dossierId);
  // LOT 9A — variants are a PV_SEUL concern; the server publishes the options and their readiness.
  const pvVariants = usePvVariants(dossierId, scenario === 'PV_SEUL');
  const selectVariant = useUpdateDossier(dossierId);

  // The currently-active document — always seeded from the server's authoritative response.
  const [doc, setDoc] = useState<DocumentDto | null>(null);

  const [documentType, setDocumentType] = useState<DocumentType>('PV_BATI');
  const [calcRunId, setCalcRunId] = useState('');
  const [scenarioCalcRunId, setScenarioCalcRunId] = useState('');
  const [scenarioDocs, setScenarioDocs] = useState<DocumentDto[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [showManifest, setShowManifest] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const manifest = useDocumentManifest(doc?.id ?? null, showManifest && doc !== null);
  // Finalization is gated on the SERVER's live pdfaStatus (post-pipeline), never re-derived client-side.
  const pipeline = usePipelineStatus(doc?.id ?? null, doc !== null);

  const calcRuns = calculations.data?.data ?? [];
  const eligibleTemplates = (templates.data ?? []).filter((t) => t.documentType === documentType);

  const canGenerate = calcRunId.trim() !== '' && !generate.isPending;

  const runGenerate = (): void => {
    if (!canGenerate) return;
    generate.mutate(
      {
        calcRunId: calcRunId.trim(),
        documentType,
        ...(templateId.trim() !== '' ? { templateId: templateId.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          setDoc(data);
          setShowManifest(false);
          setDownloadError(null);
        },
      },
    );
  };

  const locked = statut !== undefined && LOCKED_STATUSES.includes(statut);
  // LOT 9A — the selected variant, as the SERVER reports it (never inferred from the document type).
  const variantOptions = pvVariants.data?.variants ?? [];
  const selectedVariant = pvVariants.data?.selected ?? null;
  const selectedVariantOption: PvVariantOption | null =
    variantOptions.find((v) => v.variant === selectedVariant) ?? null;
  const expectedDocs: DocumentType[] =
    scenario === undefined
      ? []
      : scenario === 'PV_SEUL'
        ? // The type comes from the server's variant definition; unknown until a variant is chosen.
          selectedVariantOption?.documentType
          ? [selectedVariantOption.documentType as DocumentType]
          : []
        : SCENARIO_DOCUMENTS[scenario];
  // PV_SEUL now requires an explicitly selected, AVAILABLE variant — no default, no fallback.
  const variantReady =
    scenario !== 'PV_SEUL' || (selectedVariantOption !== null && selectedVariantOption.available);
  const canGenerateScenario =
    scenario !== undefined &&
    scenarioCalcRunId.trim() !== '' &&
    !generateScenario.isPending &&
    variantReady &&
    !locked;

  const runScenario = (): void => {
    if (!canGenerateScenario) return;
    generateScenario.mutate(
      { calcRunId: scenarioCalcRunId.trim() },
      { onSuccess: (data) => setScenarioDocs(data) },
    );
  };

  const runDownload = async (): Promise<void> => {
    if (!doc) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      await api.downloadDocument(doc.id);
    } catch (err) {
      setDownloadError(toDisplayError(err).message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 0. GÉNÉRATION PAR SCÉNARIO (LOT 8C) ---------------------------------- */}
      {scenario ? (
        <Card>
          <CardHeader>
            <CardTitle>Génération par scénario — {DOSSIER_SCENARIO_LABELS[scenario]}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Documents attendus :{' '}
              <span className="font-medium">
                {expectedDocs.length > 0
                  ? expectedDocs.map((t) => DOCUMENT_TYPE_LABELS[t]).join(' + ')
                  : '— (sélectionnez une variante de PV)'}
              </span>
              . Le serveur produit l’ensemble à partir d’un <strong>même calcul validé</strong> et
              garantit la cohérence PV/Rapport (valeur publiée identique) et l’héritage PV →
              Rapport.
            </p>

            {/* LOT 9A — variante de PV (scénario PV_SEUL uniquement) --------------- */}
            {scenario === 'PV_SEUL' ? (
              <div className="space-y-2 rounded border border-slate-200 p-3">
                <Label htmlFor="pv-variant">Variante de PV</Label>
                <Select
                  id="pv-variant"
                  value={selectedVariant ?? ''}
                  onChange={(e) =>
                    selectVariant.mutate({
                      pvVariant: e.target.value === '' ? null : (e.target.value as PvVariant),
                    })
                  }
                  disabled={locked || selectVariant.isPending}
                >
                  <option value="">— Sélectionner une variante —</option>
                  {variantOptions.map((v) => (
                    <option key={v.variant} value={v.variant}>
                      {v.label}
                      {v.available ? '' : ' (indisponible)'}
                    </option>
                  ))}
                </Select>

                {selectedVariantOption ? (
                  <div className="space-y-1 text-sm text-slate-600">
                    <p>
                      Valeur publiée :{' '}
                      <span className="font-medium">{selectedVariantOption.publishes}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      Règle : {selectedVariantOption.rule} · Politique {pvVariants.data?.policyId}@
                      {pvVariants.data?.policyVersion}
                    </p>
                    <details>
                      <summary className="cursor-pointer text-xs text-slate-500">Prérequis</summary>
                      <ul className="ml-4 list-disc text-xs">
                        {selectedVariantOption.prerequisites.map((pr) => (
                          <li key={pr}>{pr}</li>
                        ))}
                      </ul>
                    </details>
                  </div>
                ) : (
                  <Alert variant="info">
                    Le scénario PV seul exige une variante explicitement sélectionnée : aucune
                    variante n’est appliquée par défaut.
                  </Alert>
                )}

                {selectedVariantOption && !selectedVariantOption.available ? (
                  <Alert variant="error">{selectedVariantOption.unavailableReason}</Alert>
                ) : null}

                {selectVariant.isError ? (
                  <Alert variant="error" details={toDisplayError(selectVariant.error).details}>
                    {toDisplayError(selectVariant.error).message}
                  </Alert>
                ) : null}
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="scenario-calcrun">Calcul validé (scénario)</Label>
                <Select
                  id="scenario-calcrun"
                  value={scenarioCalcRunId}
                  onChange={(e) => setScenarioCalcRunId(e.target.value)}
                  disabled={locked}
                >
                  <option value="">— Sélectionner un calcul —</option>
                  {calcRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {CALC_STATUS_LABELS[r.status]} · {money(r.finalValue)} ·{' '}
                      {new Date(r.createdAt).toLocaleDateString('fr-FR')}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {locked ? (
              <Alert variant="info">
                Le dossier est verrouillé : la génération par scénario est désactivée.
              </Alert>
            ) : null}

            <Button type="button" onClick={runScenario} disabled={!canGenerateScenario}>
              {generateScenario.isPending
                ? 'Génération…'
                : `Générer le scénario (${expectedDocs.length} document${
                    expectedDocs.length > 1 ? 's' : ''
                  })`}
            </Button>

            {generateScenario.isError ? (
              <Alert variant="error" details={toDisplayError(generateScenario.error).details}>
                {toDisplayError(generateScenario.error).message}
              </Alert>
            ) : null}

            {scenarioDocs && scenarioDocs.length > 0 ? (
              <div className="space-y-2 rounded-md border border-slate-200 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Documents générés
                </p>
                {scenarioDocs.map((d) => (
                  <div
                    key={d.id}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <span className="font-medium">{DOCUMENT_TYPE_LABELS[d.documentType]}</span>
                    <span>
                      Valeur publiée :{' '}
                      <span className="font-semibold">{money(d.publishedValue)}</span>
                    </span>
                    <Badge tone={d.inheritsFromDocumentId ? 'info' : 'neutral'}>
                      {d.inheritsFromDocumentId ? 'Hérite du PV' : 'Source directe'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* A. GÉNÉRATION -------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Génération d’un document (DOCX)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="doc-type">Type de document</Label>
              <Select
                id="doc-type"
                value={documentType}
                onChange={(e) => {
                  setDocumentType(e.target.value as DocumentType);
                  setTemplateId('');
                }}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {DOCUMENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="doc-calcrun">Calcul (validé)</Label>
              <Select
                id="doc-calcrun"
                value={calcRunId}
                onChange={(e) => setCalcRunId(e.target.value)}
              >
                <option value="">— Sélectionner un calcul —</option>
                {calcRuns.map((r) => (
                  <option key={r.id} value={r.id}>
                    {CALC_STATUS_LABELS[r.status]} · {money(r.finalValue)} ·{' '}
                    {new Date(r.createdAt).toLocaleDateString('fr-FR')}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="doc-template">Modèle (facultatif)</Label>
              <Select
                id="doc-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <option value="">— Modèle actif par défaut —</option>
                {eligibleTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} — {t.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            L’expert soumet uniquement son intention. Le serveur résout le modèle actif, construit
            le manifeste et calcule le hash officiel — le Web n’effectue aucun de ces calculs.
          </p>

          <Button type="button" onClick={runGenerate} disabled={!canGenerate}>
            {generate.isPending ? 'Génération…' : 'Générer DOCX'}
          </Button>

          {generate.isError ? (
            <Alert variant="error" details={toDisplayError(generate.error).details}>
              {toDisplayError(generate.error).message}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {/* B. RÉSULTAT / STATUT ------------------------------------------------- */}
      {doc ? (
        <>
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>{doc.displayLabel}</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={statusTone(doc.status)}>{DOCUMENT_STATUS_LABELS[doc.status]}</Badge>
                  {doc.supersedesDocumentId ? <Badge tone="warning">Remplace</Badge> : null}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Field label="Type" value={DOCUMENT_TYPE_LABELS[doc.documentType]} />
                <Field label="Version" value={String(doc.version)} />
                <Field label="Valeur publiée" value={money(doc.publishedValue)} />
                <Field label="Source de la valeur" value={doc.publishedValueSource} />
                <Field
                  label="Politique de valeur"
                  value={`${doc.publishedValuePolicyId} · v${doc.publishedValuePolicyVersion}`}
                />
                <Field label="Calcul source" value={doc.calcRunId} mono />
                <Field label="SHA-256 du DOCX" value={doc.docxSha256} mono />
                <Field label="Hash du manifeste" value={doc.manifestHash} mono />
              </dl>

              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={runDownload}
                  disabled={downloading}
                >
                  {downloading ? 'Téléchargement…' : 'Télécharger DOCX'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowManifest((v) => !v)}>
                  {showManifest ? 'Masquer le manifeste' : 'Voir le manifeste'}
                </Button>
                <Button
                  type="button"
                  onClick={() => finalize.mutate(doc.id, { onSuccess: (data) => setDoc(data) })}
                  disabled={
                    (pipeline.data?.pdfaStatus ?? doc.pdfaStatus) !== 'VALID' || finalize.isPending
                  }
                >
                  Finaliser
                </Button>
              </div>

              {/* Finalization guard: blocked until the SERVER reports the PDF/A VALID (never re-derived). */}
              {(pipeline.data?.pdfaStatus ?? doc.pdfaStatus) !== 'VALID' ? (
                <p className="text-sm text-amber-700">
                  Finalisation indisponible : elle exige un PDF/A-3b produit et validé (LOT 7B). Le
                  serveur refuse la finalisation (409) tant que le statut PDF/A n’est pas « Validé
                  ».
                </p>
              ) : null}

              {downloadError ? <Alert variant="error">{downloadError}</Alert> : null}
              {finalize.isError ? (
                <Alert variant="error" details={toDisplayError(finalize.error).details}>
                  {toDisplayError(finalize.error).message}
                </Alert>
              ) : null}

              {/* C. MANIFESTE (lecture seule) ------------------------------------ */}
              {showManifest ? (
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Manifeste (lecture seule)
                  </p>
                  {manifest.isLoading ? (
                    <p className="text-sm text-slate-500">Chargement du manifeste…</p>
                  ) : manifest.isError ? (
                    <Alert variant="error">{toDisplayError(manifest.error).message}</Alert>
                  ) : manifest.data ? (
                    <>
                      <p className="text-sm text-slate-600">
                        Schéma <span className="font-mono">{manifest.data.schemaVersion}</span> —
                        hash{' '}
                        <span className="break-all font-mono text-xs">
                          {manifest.data.manifestHash}
                        </span>
                      </p>
                      <pre className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
                        {JSON.stringify(manifest.data.manifest, null, 2)}
                      </pre>
                    </>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* B-bis. CHAÎNE PDF/A (LOT 7B) — server converts/validates, never the browser. */}
          <PdfaPipelineSection dossierId={dossierId} document={doc} />
        </>
      ) : null}

      {/* D. HISTORIQUE — documents ------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Documents du dossier</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.isLoading ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : documents.isError ? (
            <Alert variant="error">{toDisplayError(documents.error).message}</Alert>
          ) : documents.data && documents.data.data.length > 0 ? (
            <ul className="space-y-2">
              {documents.data.data.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-3 text-sm"
                >
                  <span className="font-medium text-slate-800">
                    {DOCUMENT_TYPE_LABELS[d.documentType]}
                  </span>
                  <span className="text-xs text-slate-400">v{d.version}</span>
                  <Badge tone={statusTone(d.status)}>{DOCUMENT_STATUS_LABELS[d.status]}</Badge>
                  <Badge tone={pdfaTone(d.pdfaStatus)}>{PDFA_STATUS_LABELS[d.pdfaStatus]}</Badge>
                  {d.supersedesDocumentId ? <Badge tone="warning">Remplace</Badge> : null}
                  <span className="font-semibold">{money(d.publishedValue)}</span>
                  {d.manifestHash ? (
                    <span className="font-mono text-xs text-slate-400">
                      #{d.manifestHash.slice(0, 10)}…
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-slate-500">
                    {new Date(d.createdAt).toLocaleString('fr-FR')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Aucun document pour ce dossier.</p>
          )}
        </CardContent>
      </Card>

      {/* E. HISTORIQUE — exécutions de génération ---------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Exécutions de génération</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.isLoading ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : runs.isError ? (
            <Alert variant="error">{toDisplayError(runs.error).message}</Alert>
          ) : runs.data && runs.data.length > 0 ? (
            <ul className="space-y-2">
              {runs.data.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-3 text-sm"
                >
                  <Badge tone={r.status === 'SUCCESS' ? 'success' : 'danger'}>{r.status}</Badge>
                  {r.docxSha256 ? (
                    <span className="font-mono text-xs text-slate-400">
                      DOCX #{r.docxSha256.slice(0, 10)}…
                    </span>
                  ) : null}
                  {r.error ? <span className="text-xs text-rose-600">{r.error}</span> : null}
                  <span className="ml-auto text-xs text-slate-500">
                    {new Date(r.createdAt).toLocaleString('fr-FR')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Aucune exécution de génération.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
